import React, { useState, useEffect } from "react";
import { useUser } from "../../contexts";
import AccountLayout from "../../layouts/AccountLayout";
import { useRequest } from "ahooks";
import { updateOrderApi, checkOrder, logout } from "../../api";
import {
  Position,
  StatusConst,
  UserOrder,
  PositionImage,
  UploadStatus,
  ImageFile,
} from "../../interfaces";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Loader } from "@mantine/core";
import DangerModal from "../dialogs/DangerModal";
import useOrderPosition from "../../hooks/useOrderPosition";
import { Storage } from "aws-amplify";
import OrderAddressCard from "./OrderAddressCard";
import useOrder from "../../hooks/useOrder";
import AccountPosition from "./AccountPosition";
import AccountUploadNotes from "./AccountUploadNotes";
import SendItemPositions from "./SendItemPositions";
import AccountButtonBar from "./AccountButtonBar";
import ConfirmationModal from "./ConfirmationModal";
import QuantityModal from "../AccountBulkUpload/QuantityModal";
import { getRemainingCount } from "../../utils";
//import HelpSection from "./HelpSection";
import { PositionProvider } from "./PositionContext";
import SuccessScreen from "./SuccessScreen";
import { useHistory } from "react-router-dom";
export type UploadFileProps = {
  progress?: number;
  uploadStatus?: UploadStatus;
  file: File;
  positionImage?: PositionImage;
  positionIndex?: number;
  key: string;
  name: string;
};
const mapToImageFileInput = (files: ImageFile[]) => {
  return files.map((file: any) => ({
    key: file.key,
    name: file.name ?? file.file.name,
  }));
};

const Account: React.FC = () => {
  const [{ order }, dispatchUser] = useUser();
  const { handleOrderUpdate } = useOrder();

  const [sent, setSent] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showQuantityModal, setShowQuantityModal] = useState<boolean>(false);
  const [uploadedFileQuantity, setUploadedFileQuantity] = useState<number>(0);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  const [btnStatus, setBtnStatus] = useState<boolean>(false);
  // States for the notes section
  const [furtherInfo, setFurtherInfo] = useState<string>(() => {
    const savedFurtherInfo = localStorage.getItem("furtherInfo");
    return savedFurtherInfo ? savedFurtherInfo : "";
  });

  const [files, setFiles] = useState<ImageFile[]>(() => {
    const savedFiles = localStorage.getItem("files");
    if (savedFiles === "undefined") return [];
    return savedFiles ? JSON.parse(savedFiles) : [];
  });

  const [furtherInfoFlowingMotifs, setFurtherInfoFlowingMotifs] =
    useState<boolean>(() => {
      const savedValue = localStorage.getItem("furtherInfoFlowingMotifs");
      return savedValue ? JSON.parse(savedValue) : false;
    });

  const [furtherInfoQualityCheck, setFurtherInfoQualityCheck] =
    useState<boolean>(() => {
      const savedValue = localStorage.getItem("furtherInfoQualityCheck");
      return savedValue ? JSON.parse(savedValue) : false;
    });

  const [confirmationChecked, setConfirmationChecked] = useState<boolean>(
    () => {
      const saved = localStorage.getItem("confirmationChecked");
      return saved ? JSON.parse(saved) : false;
    }
  );



  useEffect(() => {
    localStorage.setItem(
      "furtherInfoQualityCheck",
      JSON.stringify(furtherInfoQualityCheck)
    );
  }, [furtherInfoQualityCheck]);
  useEffect(() => {
    localStorage.setItem(
      "furtherInfoFlowingMotifs",
      JSON.stringify(furtherInfoFlowingMotifs)
    );
  }, [furtherInfoFlowingMotifs]);

  useEffect(() => {
    localStorage.setItem("furtherInfo", furtherInfo);
  }, [furtherInfo]);

  useEffect(() => {
    localStorage.setItem("files", JSON.stringify(files.length > 0 ? mapToImageFileInput(files) : []));
  }, [files]);
  useEffect(() => {
    localStorage.setItem(
      "confirmationChecked",
      JSON.stringify(confirmationChecked)
    );
  }, [confirmationChecked]);
  const handleConfirmationCheckedChange = (checked: boolean) => {
    setConfirmationChecked(checked);
  };
  const { t } = useTranslation();
  const { positionImageAvailable } = useOrderPosition();
  const [imagesData, setImagesDataTwo] = useState<PositionImage[]>([]);
  const {
    data,
    loading: fetching,
    mutate,
  } = useRequest<UserOrder>(() => checkOrder(), {
    ready: order?.id !== "not_found",
  });

  useEffect(() => {
    if (!data) return;
    let totalQuantitySum = 0;
    let uploadedQuantitySum = 0;
    data.positions.forEach((position) => {
      if (!position.White) {
        totalQuantitySum += position.quantity;
        uploadedQuantitySum +=
          position.positionImage?.reduce(
            (acc, curr) => acc + curr.quantity,
            0
          ) || 0;
      }
    });
    setTotalQuantity(totalQuantitySum);
    setUploadedFileQuantity(uploadedQuantitySum);
    setFurtherInfo(data.furtherInfo || "");
    setFiles(data.files || []);
    setFurtherInfoFlowingMotifs(data.furtherInfoFlowingMotifs || false);
    setFurtherInfoQualityCheck(data.furtherInfoQualityCheck || false);
  }, [data]);

  useEffect(() => {
    if (!order) return;
    handleOrderUpdate({ id: order.id, lastOpened: order.email });
  }, [order]);

  const handleRemoveFile = async (index: number, item: Position) => {
    try {
      if (item.positionImage?.length === 0) return;
      order.positions[index] = { ...item, positionImage: [] };
      await handleOrderUpdate({
        id: order?.id,
        positions: order?.positions,
        files: [],
      });
      item.positionImage?.forEach((item) => {
        Storage.remove(item.key);
        Storage.remove(item.thumbnail);
      });
      const removeDeliveryNotes = order.files.map((item) => {
        return Storage.remove(item.key);
      });
      await Promise.all(removeDeliveryNotes);
    } catch (error) {
      if (error && (error as Error).message) {
        toast.error((error as Error).message);
      } else {
        toast.error("We are unable to process this request.");
      }
    }
  };

  useEffect(() => {
    if (data) {
      areAllPositionsFilled();
    }
  }, [data]);

  const areAllPositionsFilled = () => {
    if (!data) return false;
    return data.positions.every((position) => {
      const remainingCount = getRemainingCount(position);
      return remainingCount === 0;
    });
  };

  const { run } = useRequest(updateOrderApi, {
    manual: true,
    onSuccess: (value) => {
      if (value) {
        dispatchUser({
          type: "UPDATE",
          order: value,
        });
        setSent(true);
      }
    },
  });
  const history = useHistory();
  const sendPrintData = async () => {
    if (!data) return;
    const sanitizedFiles =
      files.length > 0 ? mapToImageFileInput(files) : [];
    const payload = {
      id: data.id,
      status:
        data.status === StatusConst.Error ||
          data.status === StatusConst.ReUploaded
          ? StatusConst.ReUploaded
          : StatusConst.Uploaded,
      uploadedDate: new Date().toISOString(),
      ...(furtherInfo
        ? furtherInfo === order?.furtherInfo
          ? {}
          : { furtherInfo }
        : { furtherInfo: "" }),
      ...(files.length > 0 ? { files: sanitizedFiles } : {}),
      furtherInfoFlowingMotifs,
      furtherInfoQualityCheck,
    };
    try {
      const result = await run(payload);
      if (!result) return;
      setSent(true);
      setTimeout(() => {
        logout();
        dispatchUser({ type: "SIGN_OUT" });
        history.push("/sended");
      }, 500);
    } catch (error) {
      toast.error("Unerwarteter Fehler beim Upload");
    }
  };


  const handleConfirm = async () => {
    setShowQuantityModal(false);
    sendPrintData();
  };

  const handleCancel = async () => {
    setShowQuantityModal(false);
  };
  const tabs = [
    {
      label: t("menubar.0"),
      component: (
        <AccountPosition
          setImagesDataTwo={setImagesDataTwo}
          setButtonStatus={function (isEnable: boolean) {
            setBtnStatus(isEnable);
          }}
        />
      ),
    },
    {
      label: t("menubar.1"),
      component: (
        <AccountUploadNotes
          order={order}
          furtherInfo={furtherInfo}
          setFurtherInfo={setFurtherInfo}
          files={files}
          setFiles={setFiles}
          furtherInfoFlowingMotifs={furtherInfoFlowingMotifs}
          setFurtherInfoFlowingMotifs={setFurtherInfoFlowingMotifs}
          furtherInfoQualityCheck={furtherInfoQualityCheck}
          setFurtherInfoQualityCheck={setFurtherInfoQualityCheck}
        />
      ),
    },
    {
      label: t("menubar.2"),
      component: (
        <SendItemPositions
          furtherInfo={furtherInfo}
          files={files}
          furtherInfoFlowingMotifs={furtherInfoFlowingMotifs}
          furtherInfoQualityCheck={furtherInfoQualityCheck}
          setFurtherInfoFlowingMotifs={setFurtherInfoFlowingMotifs}
          setFurtherInfoQualityCheck={setFurtherInfoQualityCheck}
          confirmationChecked={confirmationChecked}
          setConfirmationChecked={setConfirmationChecked}
          onConfirmationCheckedChange={handleConfirmationCheckedChange}
        />
      ),
    },
  ];
  return (
    <AccountLayout account>
      {order.chooseUpload && !positionImageAvailable() && (
        <DangerModal
          handleDelete={() =>
            data?.positions.forEach((item, index) =>
              handleRemoveFile(index, item)
            )
          }
        />
      )}
      {!sent ? (
        <>
          {!fetching && data && data.id !== "not_found" ? (
            <div className="">
              <p className="section-title">
                {t("Order")} {data.id}
              </p>
              <div className="flex flex-col md:flex-row gap-10 mb-20 mt-4">
                <div className="flex-1 flex">
                  <OrderAddressCard
                    data={data}
                    isShippingAddressEditable={false}
                    onUpdateOrder={(order) => mutate(order)}
                  />
                </div>
                <div className="flex-1 flex">
                  <div className="card-auto pb-8 px-8 pt-6 grid print-data-upload upload-instruction w-full">
                    <p className="text-sm font-bold mb-2">{t("instructions")}</p>
                    <p className="text-sm mb-3 whitespace-normal wrap-break-word">
                      {t("instructionContent")}
                    </p>
                    <p className="text-sm whitespace-normal wrap-break-word">
                      <strong>{t("serviceTime")}:</strong> {t("uploadTime")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid py-12 pt-6">
                <PositionProvider>
                  <AccountButtonBar
                    tabs={tabs}
                    sendPrintData={sendPrintData}
                    allPositionsFilled={btnStatus}
                    imagesData={imagesData}
                    confirmationChecked={confirmationChecked}
                    sent={sent}
                  />
                </PositionProvider>
              </div>
            </div>
          ) : (
            <div className="row row-center mt-30">
              <Loader />
            </div>
          )}
        </>
      ) : (
        <div className="sent-printdata">
          <SuccessScreen />
        </div>
      )}

      <ConfirmationModal
        modalVisible={showConfirmModal}
        setModalVisible={setShowConfirmModal}
        onConfirm={sendPrintData}
        onCancel={() => setShowConfirmModal(false)}
      />

      <QuantityModal
        modalVisible={showQuantityModal}
        setModalVisible={setShowQuantityModal}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        uploadedQuantity={uploadedFileQuantity}
        orderedQuantity={totalQuantity}
      />
    </AccountLayout>
  );
};

export default Account;